/**
 * @file OrderPickingShortage.tsx
 * @description Недостача по сборке в карточке заказа (phase19.14, экран C).
 *
 * Показывает менеджеру, чего именно не хватило, и даёт единственное рабочее
 * решение на сегодня — вернуть задание в очередь (склад пересчитает или найдёт
 * товар). Три решения бизнес-уровня (замена / частичная отгрузка / отмена
 * заказа) нарисованы выключенными: менеджер должен видеть, что выбор за ним и
 * какой именно, а сама механика приходит в phase19.17 (F31).
 *
 * Возврат в очередь — операция админа склада (SHOP_WMS_MANAGE): сервер
 * сохраняет собранные количества, снимает отметки недостачи и делает статус
 * задания снова «Новым» (`picking-tasks.service.ts#release`).
 */

import { RotateCcw } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import {
  PICKING_SHORTAGE_REASON_LABEL,
  formatPickItemName,
  pickingApi,
  type PickingTaskDetailDto,
} from "../../lib/api-picking";
import { formatItemQty } from "../../lib/picking-format";
import { wmsErrorText } from "../../lib/wms-error-text";

/** Решения менеджера по недостаче — появятся в phase19.17 (F31). */
const SHORTAGE_DECISIONS = [
  "Заменить позицию",
  "Отгрузить частично",
  "Отменить заказ",
] as const;

interface Props {
  task: PickingTaskDetailDto;
  /** Право SHOP_WMS_MANAGE — без него возврат в очередь не показываем. */
  canManage: boolean;
  /** Перечитать задание после возврата в очередь. */
  onReleased: () => void;
}

export function OrderPickingShortage({ task, canManage, onReleased }: Props) {
  const notify = useNotify();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = task.items.filter((i) => Number(i.shortageQty) > 0);
  // Решения показываем только по закрытому исходу «недостача»: пока сборка
  // идёт, отметки ещё могут измениться.
  const decidable = task.status === "SHORTAGE";

  async function release() {
    setBusy(true);
    try {
      await pickingApi.release(task.id);
      notify.success("Задание возвращено в очередь — склад соберёт заново");
      setConfirm(false);
      onReleased();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось вернуть задание в очередь"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {items.length > 0 ? (
        <div className="mt-3 rounded-lg bg-destructive/8 p-3 text-sm">
          <div className="font-medium text-destructive">
            Не хватило по {items.length} позициям:
          </div>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-on-surface-variant">
            {items.map((item) => (
              <li key={item.id}>
                {/* Количество всегда с единицей строки (§3.2): «1 / 3» без неё
                    менеджер читал как штуки, даже когда в позиции граммы. */}
                {formatPickItemName(item)} —{" "}
                {formatItemQty(item.shortageQty, item.qtyPlanned, item.unit)}
                {item.shortageReason
                  ? ` · «${PICKING_SHORTAGE_REASON_LABEL[item.shortageReason]}»`
                  : ""}
              </li>
            ))}
          </ul>
          {task.shortageNote ? (
            <p className="mt-2 text-on-surface-variant">{task.shortageNote}</p>
          ) : null}
        </div>
      ) : null}

      {decidable ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {canManage ? (
            <Button
              variant="outline"
              size="sm"
              className="min-h-12"
              onClick={() => setConfirm(true)}
              disabled={busy}
            >
              <RotateCcw className="size-4" />
              <span className="ml-1.5">Вернуть задание в очередь</span>
            </Button>
          ) : null}
          {SHORTAGE_DECISIONS.map((label) => (
            <Button
              key={label}
              variant="outline"
              size="sm"
              disabled
              className="min-h-12"
              title="Решения менеджера появятся в phase19.17"
            >
              {label} (скоро)
            </Button>
          ))}
        </div>
      ) : null}

      <AlertDialog open={confirm} onOpenChange={(v) => (!v ? setConfirm(false) : null)}>
        <AlertDialogContent fullScreen className="max-w-md gap-3">
          <AlertDialogHeader>
            <AlertDialogTitle>Вернуть задание в очередь?</AlertDialogTitle>
            <AlertDialogDescription>
              Задание {task.number} снова станет «Новым», и его сможет взять любой
              кладовщик. Собранные количества сохранятся, отметки недостачи будут
              сняты — кладовщик пройдёт задание заново. Заказ остаётся на стадии
              «На сборке».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="ghost"
              className="min-h-12"
              onClick={() => setConfirm(false)}
              disabled={busy}
            >
              Не сейчас
            </Button>
            <Button className="min-h-12" onClick={() => void release()} disabled={busy}>
              Вернуть в очередь
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
